import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import logoImage from '../assets/images/logo.png'; // Adjust the path as needed

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 5%;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgb(0, 0, 0);
  backdrop-filter: blur(8px);
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.1rem;
  text-decoration: none;
  color: white;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  margin-right: 5px;
  object-fit: contain;
`;

const LogoText = styled.span`
  font-size: 1.5rem;
  font-weight: bold;
  
  &:hover {
    text-decoration: none;
  }
`;

const NavLinksContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  z-index: 101;
  padding: 0.5rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const StyledLink = styled(Link)`
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  font-size: 0.85rem;
  position: relative;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s ease;
  padding: 0.5rem 0;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }

  &.active {
    color: rgba(255, 255, 255, 1);
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

// Add mobile menu styling
const MobileMenu = styled.div`
  display: none;
  position: fixed;
  top: 70px;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.95);
  backdrop-filter: blur(10px);
  padding: 1rem;
  flex-direction: column;
  z-index: 99;
  
  @media (max-width: 768px) {
    display: ${props => props['data-isopen'] === 'true' ? 'flex' : 'none'};
  }
`;

const MobileMenuParent = styled.div`
  color: white;
  padding: 1rem;
  font-size: 1.2rem;
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  position: relative;

  &:after {
    content: '▼';
    font-size: 0.8rem;
    position: absolute;
    right: 30%;
    transform: ${props => props.$isExpanded ? 'rotate(180deg)' : 'rotate(0)'};
    transition: transform 0.3s ease;
  }
`;

const MobileMenuLink = styled(Link)`
  color: white;
  text-decoration: none;
  padding: 1rem;
  font-size: 1.2rem;
  transition: color 0.3s;
  text-align: center;
  
  &:hover {
    color: #ccc;
  }
  
  &.active {
    color: #fff;
    font-weight: bold;
  }

  &.submenu {
    font-size: 1.1rem;
    padding-left: 2rem;
    color: rgba(255, 255, 255, 0.8);
    display: ${props => props.$isExpanded ? 'block' : 'none'};
  }
`;

const DropdownContent = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%) translateY(-10px);
  background: rgb(15, 15, 15);
  min-width: 280px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  padding: 0.75rem 0;
  
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 0;
    right: 0;
    height: 4px;
    background: transparent;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;

  &:hover ${DropdownContent} {
    opacity: 1;
    visibility: visible;
    transform: translateX(-50%) translateY(0);
  }
`;

const DropdownButton = styled(StyledLink)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  pointer-events: ${({ $isToggleOnly }) => $isToggleOnly ? 'none' : 'auto'};
  
  &::after {
    content: '▼';
    font-size: 0.6rem;
    margin-left: 0.3rem;
    opacity: 0.6;
    transition: transform 0.3s ease;
  }

  ${DropdownContainer}:hover &::after {
    transform: rotate(180deg);
  }

  &.active {
    color: rgba(255, 255, 255, 1);
  }
`;

const DropdownItem = styled(Link)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  color: ${props => props.$isActive ? '#fff' : 'rgba(255, 255, 255, 0.7)'};
  text-decoration: none;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const ComingSoonTag = styled.span`
  font-size: 0.7rem;
  padding: 2px 8px;
  border-radius: 12px;
  background: rgba(255, 77, 0, 0.2);
  color: #FF4D00;
  margin-left: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
`;

const JoinButton = styled.a`
  background: none;
  border: none;
  color: white;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  margin-left: 1rem;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const MobileJoinButton = styled.a`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 1rem;
  transition: all 0.3s ease;
  text-align: center;
  width: 100%;

  &:hover {
    color: rgba(255, 255, 255, 1);
  }
`;

const MenuIcon = styled.span`
  font-size: 1.5rem;
`;

const MoreComingSoonItem = styled.div`
  padding: 12px 24px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 8px;
  text-align: center;
  font-style: italic;
`;

function Navbar() {
  const location = useLocation();
  const [activeLink, setActiveLink] = useState(() => {
    // Set initial active link based on current path
    if (location.pathname.startsWith('/geo-os') || location.pathname === '/partner-program') return 'Geo OS';
    if (location.pathname.startsWith('/platform') || location.pathname.includes('operations')) return 'Platforms';
    if (location.pathname === '/manifesto') return 'Manifesto';
    if (location.pathname === '/') return 'Home';
    return '';
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  
  // Update active link whenever location changes
  React.useEffect(() => {
    if (location.pathname.startsWith('/geo-os') || location.pathname === '/partner-program') {
      setActiveLink('Geo OS');
    } else if (location.pathname.startsWith('/platform') || location.pathname.includes('operations')) {
      setActiveLink('Platforms');
    } else if (location.pathname === '/manifesto') {
      setActiveLink('Manifesto');
    } else if (location.pathname === '/') {
      setActiveLink('Home');
    } else {
      setActiveLink('');
    }
  }, [location.pathname]);
  
  const navLinks = [
    { text: 'Home', href: '/' },
    { 
      text: 'Geo OS', 
      href: '/geo-os',
      dropdownItems: [
        { text: 'Overview', href: '/geo-os' },
        { text: 'Partner Program', href: '/partner-program' }
      ]
    },
    { 
      text: 'Platforms', 
      href: '/platform', 
      dropdownItems: [
        { text: 'Airfield Operations', href: '/platform/airfield-operations', isReady: true },
        { text: 'Maritime Operations', href: '/platform/maritime-operations', isReady: false }
      ]
    },
    { text: 'Manifesto', href: '/manifesto' }
  ];

  const handleLinkClick = (text) => {
    setActiveLink(text);
    setMobileMenuOpen(false);
  };
  
  const toggleSubmenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  return (
    <>
      <Nav>
        <LogoContainer as={Link} to="/">
          <Logo src={logoImage} alt="GeoSpatios Logo" />
          <LogoText>GeoSpatios</LogoText>
        </LogoContainer>
        
        <NavLinksContainer>
          {navLinks.map((link, index) => (
            link.dropdownItems ? (
              <DropdownContainer key={index}>
                <DropdownButton
                  as="div"
                  onClick={() => toggleSubmenu(link.text)}
                  className={activeLink === link.text ? 'active' : ''}
                >
                  {link.text}
                </DropdownButton>
                <DropdownContent $isOpen={expandedMenus[link.text]}>
                  {link.dropdownItems.map((item, index) => (
                    <DropdownItem
                      key={index}
                      to={item.href}
                      onClick={() => {
                        toggleSubmenu(link.text);
                        handleLinkClick(link.text);
                      }}
                      $isActive={location.pathname === item.href}
                    >
                      {item.text}
                      {item.isReady === false && <ComingSoonTag>Coming Soon</ComingSoonTag>}
                    </DropdownItem>
                  ))}
                  {link.text === 'Platforms' && <MoreComingSoonItem>More sectors coming soon...</MoreComingSoonItem>}
                </DropdownContent>
              </DropdownContainer>
            ) : (
              <StyledLink
                key={index}
                to={link.href}
                className={activeLink === link.text ? 'active' : ''}
                onClick={() => handleLinkClick(link.text)}
              >
                {link.text}
              </StyledLink>
            )
          ))}
        </NavLinksContainer>
        
        <RightSection>
          <StyledLink to="/careers">Careers</StyledLink>
        </RightSection>
        
        <MobileMenuButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
          <MenuIcon>☰</MenuIcon>
        </MobileMenuButton>
      </Nav>
      
      {/* Mobile Menu */}
      <MobileMenu data-isopen={mobileMenuOpen.toString()}>
        <MobileMenuLink to="/" onClick={() => handleLinkClick('Home')}>
          Home
        </MobileMenuLink>
        
        <MobileMenuParent 
          $isExpanded={expandedMenus['Geo OS']} 
          onClick={() => toggleSubmenu('Geo OS')}
        >
          Geo OS
        </MobileMenuParent>
        <MobileMenuLink 
          to="/geo-os" 
          className="submenu"
          $isExpanded={expandedMenus['Geo OS']}
          onClick={() => handleLinkClick('Geo OS')}
        >
          Overview
        </MobileMenuLink>
        <MobileMenuLink 
          to="/partner-program" 
          className="submenu"
          $isExpanded={expandedMenus['Geo OS']}
          onClick={() => handleLinkClick('Geo OS')}
        >
          Partner Program
        </MobileMenuLink>

        <MobileMenuParent 
          $isExpanded={expandedMenus['Platforms']} 
          onClick={() => toggleSubmenu('Platforms')}
        >
          Platforms
        </MobileMenuParent>
        <MobileMenuLink 
          to="/platform/airfield-operations" 
          className="submenu"
          $isExpanded={expandedMenus['Platforms']}
          onClick={() => handleLinkClick('Platforms')}
        >
          Airfield Operations
        </MobileMenuLink>
        <MobileMenuLink 
          to="/platform/maritime-operations" 
          className="submenu"
          $isExpanded={expandedMenus['Platforms']}
          onClick={() => handleLinkClick('Platforms')}
        >
          Maritime Operations
        </MobileMenuLink>

        <MobileMenuLink to="/manifesto" onClick={() => handleLinkClick('Manifesto')}>
          Manifesto
        </MobileMenuLink>
        
        <MobileMenuLink to="/careers">
          Careers
        </MobileMenuLink>
      </MobileMenu>
    </>
  );
}

export default Navbar; 